import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Loader2, Users, Thermometer } from 'lucide-react';
import { searchLeads } from '../../services/salesCoachContextService';

interface LeadSearchSelectorProps {
  onSelect: (lead: any) => void;
  onClose: () => void;
}

export function LeadSearchSelector({ onSelect, onClose }: LeadSearchSelectorProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setHasSearched(true);
    const data = await searchLeads(query);
    setResults(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, doSearch]);

  const getTempBadge = (temp: string) => {
    const t = temp?.toLowerCase() || '';
    if (t.includes('hot')) return { bg: 'bg-red-50 text-red-600 border-red-200', label: 'Hot' };
    if (t.includes('warm')) return { bg: 'bg-amber-50 text-amber-600 border-amber-200', label: 'Warm' };
    if (t.includes('cold')) return { bg: 'bg-blue-50 text-blue-600 border-blue-200', label: 'Cold' };
    return null;
  };

  const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('active') || s.includes('engaged')) return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    if (s.includes('new')) return 'bg-blue-50 text-blue-600 border-blue-200';
    if (s.includes('dead') || s.includes('lost') || s.includes('not_interested')) return 'bg-gray-100 text-gray-500 border-gray-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatStatus = (status: string) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown';
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Search for a Lead</h3>
            <p className="text-sm text-gray-500 mt-0.5">Find any lead by company name or contact</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Type company name or contact name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-sm"
              autoFocus
            />
          </div>
        </div>

        <div className="px-4 pb-4 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#1875FF] animate-spin" />
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-12">
              <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">
                Start typing to search leads
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">
                No leads found for "{search}"
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((lead) => {
                const temp = getTempBadge(lead.ovrall_lead_temp);
                return (
                  <button
                    key={lead.lead_id}
                    onClick={() => onSelect(lead)}
                    className="w-full flex items-center gap-4 p-3.5 rounded-xl border border-gray-100 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5 transition-all text-left group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center">
                      <Users size={20} className="text-[#1875FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate group-hover:text-[#1875FF] transition-colors">
                        {lead.company_name || 'Unknown Company'}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">
                        {lead.dm_full_name || 'No contact'}{lead.dm_title ? ` - ${lead.dm_title}` : ''}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {lead.ovrall_lead_status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getStatusBadge(lead.ovrall_lead_status)}`}>
                            {formatStatus(lead.ovrall_lead_status)}
                          </span>
                        )}
                        {temp && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 ${temp.bg}`}>
                            <Thermometer size={10} />
                            {temp.label}
                          </span>
                        )}
                        {lead.industry_1 && (
                          <span className="text-xs text-gray-400">
                            {lead.industry_1}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
